import * as React from "react";
import {Box, Card, CardContent, IconButton, Stack, Typography} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import type {TaskDto} from "../../../../types/task";

import {fmtDateTime, fmtInt, fmtPct} from "../../../../utils/utils";

// ---------- local utils/presentational ----------


const StatusDot: React.FC<{ color?: string }> = ({color = "success.main"}) => (
  <Box
    component="span"
    sx={{display: "inline-block", width: 10, height: 10, borderRadius: "50%", bgcolor: color, mr: 1}}
  />
);

const Metric: React.FC<{ label?: string; value: number; sub?: string; align?: "left" | "right" }> = ({
                                                                                                       label,
                                                                                                       value,
                                                                                                       sub,
                                                                                                       align = "right",
                                                                                                     }) => (
  <Box sx={{textAlign: align}}>
    {label && <Typography variant="body2" color="text.secondary">{label}</Typography>}
    <Typography variant="h6" sx={{lineHeight: 1.2}}>{fmtInt(value)}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Box>
);

const getStatusColor = (status: TaskDto["status"]): string => {
  switch (status) {
    case "NEW":
      return "warning.main";
    case "RUNNING":
      return "success.main";
    case "FINISHED":
      return "info.main";
    case "SUSPENDED":
      return "error.main";
    default:
      return "grey.500";
  }
};

// ---------- component ----------
export default function CampaignCard({
                                       row,
                                       onOpenMenu,
                                     }: {
  row: TaskDto;
  onOpenMenu: (e: React.MouseEvent<HTMLButtonElement>, row: TaskDto) => void;
}) {
  const total = row.total ?? 0;
  const sent = row.sent ?? 0;
  const opened = row.opened ?? 0;
  const bounced = row.bounced ?? 0;
  const unsubscribed = row.unsubscribed ?? 0;

  return (
    <Card variant="outlined" sx={{borderRadius: 3, outline: "none"}}>
      <CardContent>
        <Stack
          direction={{xs: "column", md: "row"}}
          spacing={2}
          alignItems={{xs: "flex-start", md: "center"}}
          justifyContent="space-between"
        >
          {/* Left: meta */}
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{minWidth: 280, flex: 1}}>
            <Stack spacing={0.5}>
              <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                {row.subject}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <StatusDot color={getStatusColor(row.status)}/>
                <Typography variant="body2" sx={{mr: 1}}>{row.status}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Created at {fmtDateTime(row.createdAt)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {/* Middle: metrics */}
          <Stack
            direction={{xs: "column", sm: "row"}}
            spacing={{xs: 1.5, sm: 4}}
            useFlexGap
            flexWrap="wrap"
            sx={{flex: 2}}
          >
            <Metric label="Total emails" value={total} sub="100%"/>
            <Metric label="Sent emails" value={sent} sub={fmtPct(sent, total)}/>
            <Metric label="Opened" value={opened} sub={fmtPct(opened, sent)}/>
            <Metric label="Unsubscribed" value={unsubscribed} sub={fmtPct(unsubscribed, sent)}/>
            <Metric label="Bounced" value={bounced} sub={fmtPct(bounced, sent)}/>
          </Stack>

          {/* Planned time */}
          <Stack direction={{xs: "column", sm: "row"}} spacing={{xs: 1.5, sm: 4}} useFlexGap flexWrap="wrap">
            <Box>
              <Typography>Planned</Typography>
              <Typography variant="body2" color="text.secondary">
                {fmtDateTime(row.planedDate)}
              </Typography>
            </Box>
          </Stack>

          {/* Right: actions */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{minWidth: 72, justifyContent: {xs: "flex-start", md: "flex-end"}}}
          >
            <IconButton aria-label="Engagement chart"><ShowChartIcon/></IconButton>
            <IconButton aria-label="More actions" onClick={(e) => onOpenMenu(e, row)}>
              <MoreVertIcon/>
            </IconButton>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
